import {createBinding, syncYjsChangesToLexical} from '@lexical/yjs';
import * as Sentry from '@sentry/react';
import {$getRoot, LexicalEditor} from 'lexical';
import type {YEvent} from 'yjs';
import * as Y from 'yjs';

export const decodeYjsAndInsertContent = (
  editor: LexicalEditor,
  yjsData: Uint8Array,
  onReady?: (editorNodes: ChildNode[]) => void,
  source?: string
) => {
  try {
    // Create a temporary Yjs document
    const tempDoc = new Y.Doc();

    // Create a temporary binding to extract the Lexical state
    const tempYjsDocMap = new Map<string, Y.Doc>();
    tempYjsDocMap.set('main', tempDoc);

    // Create a mock provider (we don't need actual sync)
    const mockProvider = {
      awareness: {
        getStates: () => new Map(),
        setLocalState: () => {},
        setLocalStateField: () => {},
        on: () => {},
        off: () => {},
      },
      on: () => {},
      off: () => {},
      connect: () => {},
      disconnect: () => {},
    };

    // Create the binding BEFORE applying the update
    const binding = createBinding(editor, mockProvider as any, 'main', tempDoc, tempYjsDocMap);

    // Set up an observer to catch the changes when we apply the update
    const {root: bindingRoot} = binding;
    let didSync = false;

    const onYjsTreeChanges = (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      events: Array<YEvent<any>>
    ) => {
      if (!didSync) {
        didSync = true;
        syncYjsChangesToLexical(binding, mockProvider as any, events, false);
      }
    };

    // Observe changes to the Yjs document
    bindingRoot.getSharedType().observeDeep(onYjsTreeChanges);

    // NOW apply the binary update - this should trigger the observer
    Y.applyUpdate(tempDoc, yjsData);

    // Give it a moment to process, then trigger onReady
    setTimeout(() => {
      onReady?.([...(editor.getRootElement()?.childNodes ?? [])]);
    }, 100);
  } catch (e) {
    console.error('Failed to decode Yjs data:', e);
    if (source) {
      Sentry.captureException(new Error(`[RichTextEditor] Invalid Yjs data in ${source}`));
    }
    // Fallback to empty editor
    editor.update(() => {
      $getRoot().clear();
    });
  }
};
